import { Card, CardContent } from "@/components/ui/card";
import { ThemeToggle } from "@/components/theme-toggle";
import { AlertTriangle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import type { LegalDocumentStatus } from "@shared/schema";

export default function PrivacyPolicyPage() {
  const { data: statuses = [] } = useQuery<LegalDocumentStatus[]>({
    queryKey: ["/api/public/legal-status"],
  });
  const status = statuses.find((s) => s.documentSlug === "privacy");
  const showDraft = status?.isDraft ?? true;
  const note = status?.bannerNote;
  const reviewedAt = status?.lastReviewedAt ? new Date(status.lastReviewedAt) : null;
  const reviewedBy = status?.lastReviewedBy ?? null;
  const reviewedDateLabel = reviewedAt
    ? reviewedAt.toISOString().slice(0, 10)
    : null;
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-14 items-center justify-between px-4">
          <a href="/" className="font-semibold text-lg" data-testid="link-home">
            Clarik
          </a>
          <ThemeToggle />
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        {showDraft ? (
          <div
            className="mb-6 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-950/30"
            data-testid="banner-privacy-draft"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-600 dark:text-amber-400" />
            <div className="text-sm">
              <div className="font-semibold text-amber-900 dark:text-amber-200">
                DRAFT — pending review by legal counsel
              </div>
              <div className="text-amber-900/90 dark:text-amber-200/90">
                {note ??
                  "This Privacy Policy is published in good-faith working draft form while it is reviewed by external counsel. Final binding language is forthcoming."}
                <span className="ml-1 italic">An administrator can flip this banner off after counsel review.</span>
              </div>
            </div>
          </div>
        ) : reviewedDateLabel ? (
          <div
            className="mb-6 text-sm text-muted-foreground"
            data-testid="text-privacy-reviewed"
          >
            Last reviewed {reviewedDateLabel}
            {reviewedBy ? ` by ${reviewedBy}` : ""}.
          </div>
        ) : note ? (
          <div
            className="mb-6 text-sm text-muted-foreground"
            data-testid="text-privacy-reviewed"
          >
            {note}
          </div>
        ) : null}

        <Card>
          <CardContent className="prose dark:prose-invert max-w-none p-6 md:p-8">
            <h1 className="text-2xl md:text-3xl font-bold mb-6" data-testid="text-privacy-title">
              Privacy Policy – Clarik
            </h1>
            
            <p className="text-muted-foreground mb-8">
              Last updated: April 26, 2026
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">1. Introduction</h2>
              <p className="text-foreground/90 leading-relaxed">
                Clarik is a customer relationship management (CRM) platform designed for service-based 
                entertainment businesses. We take your privacy seriously and are committed to protecting 
                the personal information you share with us. This Privacy Policy explains how we collect, 
                use, and safeguard your data when you use our platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">2. Information We Collect</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                When you use Clarik, we may collect the following types of information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                <li>
                  <strong>Account information</strong> – Information you provide when creating and managing 
                  your account, such as your name, email address, and login credentials.
                </li>
                <li>
                  <strong>Contact and lead information</strong> – Details about your clients and leads that 
                  you enter into the CRM, including names, email addresses, phone numbers, event details, 
                  and booking information.
                </li>
                <li>
                  <strong>Email data</strong> – Email metadata and message content related to emails sent 
                  or received through connected Gmail accounts, used solely to support CRM functionality 
                  such as communication logging and opt-out processing.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">3. Use of Google User Data</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                Clarik integrates with Google Gmail to help you manage email communications with 
                your clients and leads. We want to be completely transparent about how we use this access:
              </p>
              <div className="bg-muted/50 border rounded-lg p-4 my-4">
                <p className="text-foreground/90 leading-relaxed">
                  Clarik uses Google Gmail API access only to send emails initiated by the user and 
                  to read incoming replies related to those emails. This access is used solely to log 
                  communication history inside the CRM and to honor opt-out requests (such as "stop" or 
                  "unsubscribe"). Clarik does not use Gmail data for advertising, does not sell 
                  Google user data, and does not access unrelated personal emails.
                </p>
              </div>
              <p className="text-foreground/90 leading-relaxed">
                Our use of Gmail API data complies with Google's Limited Use Requirements. We only access, 
                use, store, and share Gmail data as necessary to provide and improve user-facing features 
                that are visible and prominent in our application's user interface.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">3a. Use of Meta (Facebook &amp; Instagram) Platform Data</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                Clarik optionally connects to Meta&rsquo;s Facebook Pages and Instagram
                Business APIs to let you draft, schedule, and publish posts to your own Pages and
                Instagram Business accounts, and to read engagement (comments, likes, basic insights)
                back into the CRM. We only access data for Pages and Instagram accounts you
                explicitly connect, and only via the scopes you grant during the OAuth consent
                screen. We do not access your personal Facebook profile, your friends list, your
                Messenger inbox, or any Page/account you have not connected. Meta data is used
                solely to provide the in-product social media features and is never used for
                advertising, sold, transferred to third parties for advertising, or used to build
                profiles unrelated to your use of Clarik.
              </p>
              <p className="text-foreground/90 leading-relaxed">
                Our use of Meta Platform data complies with Meta&rsquo;s Platform Terms and
                Developer Policies, including the Limited Use Requirements for Page and Instagram
                Business data. You can disconnect your Meta accounts at any time from the Social
                Media &gt; Accounts tab.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">3b. Use of TikTok Platform Data</h2>
              <p className="text-foreground/90 leading-relaxed">
                Clarik optionally connects to TikTok&rsquo;s Login Kit and Content Posting
                API to let you publish videos to your own TikTok account and read back basic
                profile and post-status information. We only request the minimum scopes
                (<code>user.info.basic</code>, <code>video.upload</code>, <code>video.publish</code>)
                needed to support these features. TikTok data is used solely to provide the
                in-product features, is not used for advertising, is not sold, and is not shared
                with third parties for any purpose unrelated to providing the service. You can
                disconnect at any time, which revokes our access tokens and stops all data flow.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">3c. Use of YouTube Platform Data</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                Clarik uses the YouTube API Services (provided by Google) to let you upload
                videos to your own YouTube channel and to read basic channel and video status
                information. By using the YouTube features in Clarik you also agree to the{" "}
                <a
                  href="https://www.youtube.com/t/terms"
                  className="text-primary hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >YouTube Terms of Service</a>{" "}
                and the{" "}
                <a
                  href="https://policies.google.com/privacy"
                  className="text-primary hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >Google Privacy Policy</a>.
              </p>
              <p className="text-foreground/90 leading-relaxed">
                You can revoke Clarik&rsquo;s access to your YouTube data at any time via
                Google&rsquo;s security settings at{" "}
                <a
                  href="https://myaccount.google.com/permissions"
                  className="text-primary hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >https://myaccount.google.com/permissions</a>{" "}
                in addition to disconnecting from the Social Media &gt; Accounts tab inside
                Clarik.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">4. Data Sharing</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                We are committed to protecting your data and do not monetize it in any way:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                <li>
                  <strong>No selling of data</strong> – We never sell your personal information or CRM 
                  data to third parties.
                </li>
                <li>
                  <strong>No advertising use</strong> – Your data is never used for advertising purposes 
                  or shared with ad networks.
                </li>
                <li>
                  <strong>Limited service provider sharing</strong> – We may share data with trusted 
                  service providers who help us operate the platform (such as hosting and email delivery 
                  services), but only to the extent necessary and under strict confidentiality agreements.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">5. Data Security</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                We use defense-in-depth security controls to protect your information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                <li>
                  <strong>Encryption at rest:</strong> Customer data is encrypted with
                  envelope encryption (AES-256-GCM data keys wrapped by managed
                  key-encryption keys). Sensitive contact and payment fields receive an
                  additional layer of field-level encryption.
                </li>
                <li>
                  <strong>Encryption in transit:</strong> All traffic is served over
                  TLS 1.2+; integrations to third-party providers use OAuth2 over TLS.
                </li>
                <li>
                  <strong>No-loose-PII rule:</strong> We do not log full payment
                  instruments, government IDs, or audience PII to application logs,
                  error trackers, or analytics. The HTTP access logger does not
                  capture response bodies in production at all — only the request
                  method, path, status code, and duration are retained — so email
                  addresses, phone numbers, and other contact-record fields never
                  reach the long-lived log store in the first place.
                </li>
                <li>
                  <strong>Access controls:</strong> Role-based permissions, workspace
                  isolation, and least-privilege defaults govern access. Privileged
                  actions are logged for audit.
                </li>
                <li>
                  <strong>Breach notification:</strong> If a confirmed security incident
                  affects your data, we notify the workspace owner without undue delay
                  and within 72 hours of confirmation, with the information required by
                  applicable data-protection laws.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">6. Data Retention</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                We retain your information only as long as needed to provide the service
                and meet our legal obligations:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                <li><strong>Live data:</strong> Retained while your subscription is active.</li>
                <li>
                  <strong>Soft-deleted records:</strong> Recoverable for 7 days, then
                  permanently purged.
                </li>
                <li>
                  <strong>Backups:</strong> Encrypted backups are taken daily
                  (Recovery Point Objective of no more than 24 hours) and
                  retained under a weekly + monthly anchor policy: by default
                  every backup from the last twelve (12) weeks is kept, plus
                  one anchor backup from each of the prior twelve (12) months.
                  Older or non-anchor backups are pruned automatically; failed
                  or in-progress runs are preserved while still recent.
                </li>
                <li>
                  <strong>Logs &amp; audit trails:</strong> Retained for at least
                  365 days in an append-only database protected against in-place
                  edits, deletions, and table truncation. Daily off-database
                  exports are archived to encrypted object storage as an
                  additional tamper-resistance layer. We are working toward a
                  longer multi-year retention target to support financial-services
                  and SOC2 evidence requirements; the current effective floor is
                  365 days.
                </li>
                <li>
                  <strong>Account closure:</strong> You may request deletion of
                  your account and all associated data at any time via{" "}
                  <a href="/data-deletion" className="text-primary hover:underline">
                    /data-deletion
                  </a>{" "}
                  or by contacting support. Production data is permanently
                  removed within thirty (30) days of a verified request, and
                  the corresponding rows are removed from backups in the next
                  pruning cycle as the underlying weekly / monthly anchors
                  roll off. Automated post-cancellation deletion based on
                  account inactivity is an aspirational target and is not yet
                  enforced by an automated job — closures today are driven by
                  the request path described above.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">7. User Control</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                You have control over your data and how Clarik uses it:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                <li>
                  <strong>Disconnect Gmail</strong> – You can disconnect your Gmail account from 
                  Clarik at any time through the Email Hub settings. This will stop all Gmail 
                  integration features.
                </li>
                <li>
                  <strong>Disconnect social accounts</strong> – You can disconnect your Meta
                  (Facebook / Instagram), TikTok, and YouTube accounts at any time from the
                  Social Media &gt; Accounts tab. Disconnecting revokes our access tokens and
                  stops all data flow from that platform immediately.
                </li>
                <li>
                  <strong>Request data deletion</strong> – You can request deletion of your account
                  and all associated data, or platform-specific data only, via our public form at{" "}
                  <a href="/data-deletion" className="text-primary hover:underline" data-testid="link-data-deletion">
                    /data-deletion
                  </a>{" "}
                  or by contacting our support team.
                </li>
                <li>
                  <strong>Access your data</strong> – You can export your CRM data at any time using 
                  the built-in export features.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">8. Governing Law and Disputes</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                Any dispute, claim, or controversy arising out of or relating to this
                Privacy Policy &mdash; including, without limitation, claims relating to
                personal-information handling, data subject rights, or notification
                obligations under applicable state, federal, or international privacy
                statutes &mdash; is governed by the same governing-law and
                dispute-resolution terms that apply to your use of Clarik under our
                Terms of Service. Specifically, &sect;16 (Governing Law) and &sect;17
                (Dispute Resolution) of the{" "}
                <a
                  href="/terms"
                  className="text-primary hover:underline"
                  data-testid="link-terms-of-service"
                >
                  Terms of Service
                </a>{" "}
                are incorporated into this Privacy Policy by reference and apply with
                equal force to privacy-related disputes.
              </p>
              <p className="text-foreground/90 leading-relaxed mb-4">
                In summary (the Terms control if there is any conflict): this Privacy
                Policy is governed by the laws of the State of Delaware, without regard
                to its conflict-of-laws principles, and any action not subject to
                arbitration is subject to the exclusive jurisdiction of the state and
                federal courts located in New Castle County, Delaware. Privacy-related
                disputes are otherwise subject to the binding individual JAMS
                arbitration, class-action waiver, informal-resolution notice, carve-outs,
                and 30-day individual opt-out right described in &sect;17 of the Terms.
              </p>
              <p className="text-foreground/90 leading-relaxed">
                Nothing in this section limits any non-waivable right you have under
                applicable data-protection law to lodge a complaint with a competent
                supervisory authority or to pursue remedies that, by law, cannot be
                contractually arbitrated or waived.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">9. Contact Information</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or how we handle your data, 
                please contact us:
              </p>
              <div className="bg-muted/50 border rounded-lg p-4">
                <p className="text-foreground/90">
                  <strong>Email:</strong>{" "}
                  <a 
                    href="mailto:admin@entertaincrm.com" 
                    className="text-primary hover:underline"
                    data-testid="link-support-email"
                  >
                    admin@entertaincrm.com
                  </a>
                </p>
              </div>
            </section>

            <hr className="my-8 border-border" />

            <p className="text-muted-foreground text-sm">
              This Privacy Policy may be updated from time to time. We encourage you to review this 
              page periodically for any changes. Your continued use of Clarik after any 
              modifications indicates your acceptance of the updated policy.
            </p>
          </CardContent>
        </Card>
      </main>

      <footer className="border-t py-6 mt-8">
        <div className="container mx-auto px-4 text-center text-muted-foreground text-sm">
          <p>&copy; {new Date().getFullYear()} Clarik. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
